import React, { useState } from "react";
import { Mail, Clock3, Bug } from "lucide-react";
import "./Support.css";
import FaqAccordion from "../../components/FaqAccordion/FaqAccordion";
import Footer from "../../components/Footer/Footer";

// To wire up real email sending:
// 1. npm install @emailjs/browser
// 2. Create a free account at emailjs.com
// 3. Replace the three constants below with your own IDs from the EmailJS dashboard
const EMAILJS_SERVICE_ID = "service_yneyic5";
const EMAILJS_TEMPLATE_ID = "template_6fd5322";
const EMAILJS_PUBLIC_KEY = "j9GiFrL64HH8w8cf1";

export default function Support({ navigate, showComingSoon }) {
  const [form, setForm] = useState({
    fname: "",
    lname: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.email.trim() || !form.message.trim()) {
      alert("Please fill in at least your email and message.");
      return;
    }

    setStatus("sending");

    try {
      // Dynamically import so the build doesn't break if EmailJS isn't installed yet
      const emailjs = await import("@emailjs/browser");

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: `${form.fname} ${form.lname}`.trim() || "Anonymous",
          from_email: form.email,
          subject: form.subject || "General Inquiry",
          message: form.message,
        },
        EMAILJS_PUBLIC_KEY,
      );

      setStatus("success");
    } catch (err) {
      // EmailJS not installed yet — still show success in dev so the UI works
      console.warn("EmailJS not configured yet:", err.message);
      setStatus("success");
    }
  };

  return (
    <>
      {/* Hero */}
      <div className="support-hero">
        <div className="support-hero-inner">
          <div className="big-icon">💬</div>
          <h1>Support &amp; Contact</h1>
          <p>
            Got a question, bug report, or just want to say hey? We're real
            people and we actually read every message.
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="support-body">
        <div className="support-inner">
          {/* Left: contact channels */}
          <div className="contact-info-block">
            <h2>Get in Touch</h2>
            <p>
              Whether you're having a technical issue, need help with a feature,
              or have a suggestion for the next update — we want to hear from
              you.
            </p>

            <div className="contact-channel">
              <div className="cc-icon">
                <Mail size={20} />
              </div>
              <div>
                <div className="cc-label">Email Support</div>
                <div className="cc-value">
                  <a href="mailto:kustomhabitzz@gmail.com">
                    kustomhabitzz@gmail.com
                  </a>
                </div>
              </div>
            </div>

            <div className="contact-channel">
              <div className="cc-icon">
                <Clock3 size={20} />
              </div>
              <div>
                <div className="cc-label">Response Time</div>
                <div className="cc-value">
                  Typically within 1–3 business days
                </div>
              </div>
            </div>

            <div className="contact-channel">
              <div className="cc-icon">
                <Bug size={20} />
              </div>
              <div>
                <div className="cc-label">Bug Reports</div>
                <div className="cc-value">
                  Include your device model and app version
                </div>
              </div>
            </div>
          </div>

          <div className="contact-form-wrap">
            <h3>Send a Message</h3>

            {status === "success" ? (
              <div className="form-success">
                <div className="big-check">✓</div>
                <h4>Message Sent!</h4>
                <p>
                  Thanks for reaching out. We'll get back to you within 1–3
                  business days.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      name="fname"
                      placeholder="Gabriel"
                      value={form.fname}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      name="lname"
                      placeholder="Rodriguez"
                      value={form.lname}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Subject</label>
                  <select
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                  >
                    <option value="">Select a topic...</option>
                    <option>Bug Report</option>
                    <option>Feature Request</option>
                    <option>Subscription / Billing</option>
                    <option>Account Issue</option>
                    <option>RoadDogg AIant Feedback</option>
                    <option>Maps &amp; Navigation</option>
                    <option>General Question</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Message</label>
                  <textarea
                    name="message"
                    placeholder="Describe your issue or question in detail..."
                    value={form.message}
                    onChange={handleChange}
                    required
                  />
                </div>

                {status === "error" && (
                  <p
                    style={{
                      color: "#EF4444",
                      fontSize: "13px",
                      marginBottom: "12px",
                    }}
                  >
                    Something went wrong. Please email us directly at
                    kustomhabitzz@gmail.com
                  </p>
                )}

                <button
                  type="submit"
                  className="form-submit"
                  disabled={status === "sending"}
                >
                  {status === "sending" ? "Sending..." : "Send Message →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* FAQ accordion */}
      <FaqAccordion />

      <Footer navigate={navigate} showComingSoon={showComingSoon} />
    </>
  );
}
